import {
  currentTheme as currentVizTheme,
  refreshTheme,
} from 'devextreme/viz/themes';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const themes = ['light', 'dark'] as const;

type Theme = (typeof themes)[number];

function getNextTheme(theme?: Theme) {
  return themes[themes.indexOf(theme) + 1] || themes[0];
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  // Shared with the theme loader in index.html, which adds the stored theme's <link id="app-theme">
  // before bootstrap. Only that one theme is downloaded; a switch swaps the link.
  private storageKey = 'app-theme';
  private linkId = 'app-theme';
  private pendingLink?: HTMLLinkElement;

  currentTheme: Theme =
    window.localStorage[this.storageKey] === 'dark' ? 'dark' : 'light';

  public isDark = new BehaviorSubject<boolean>(this.currentTheme === 'dark');

  // Hashed file names in a build (scripts/hash-theme-files.js), plain ones under ng serve.
  private getThemeHref(theme: Theme) {
    return (
      document.getElementById('theme-loader')?.getAttribute(`data-${theme}`) ??
      `theme-${theme}.css`
    );
  }

  private loadStyleSheet(theme: Theme, onApplied: () => void) {
    this.pendingLink?.remove();
    this.pendingLink = undefined;

    const href = this.getThemeHref(theme);
    const current = document.getElementById(this.linkId) as HTMLLinkElement;
    if (current?.getAttribute('href') === href) {
      onApplied();
      return;
    }

    // The old sheet stays until the new one has loaded, so the page is never unstyled mid-switch.
    const next = document.createElement('link');
    next.rel = 'stylesheet';
    next.href = href;
    next.onload = () => {
      if (this.pendingLink !== next) return;
      this.pendingLink = undefined;
      current?.remove();
      next.id = this.linkId;
      onApplied();
    };
    next.onerror = () => {
      if (this.pendingLink === next) this.pendingLink = undefined;
      next.remove();
      console.error(`Theme stylesheet ${href} failed to load.`);
    };
    this.pendingLink = next;
    if (current) current.after(next);
    else document.body.prepend(next);
  }

  setAppTheme(theme = this.currentTheme) {
    this.currentTheme = theme;
    this.isDark.next(this.currentTheme === 'dark');

    this.loadStyleSheet(theme, () => {
      currentVizTheme(
        currentVizTheme().replace(/\.[a-z]+\.compact$/, `.${theme}.compact`),
      );
      refreshTheme();
    });
  }

  getCurrentTheme() {
    return this.currentTheme;
  }

  switchTheme() {
    const newTheme = getNextTheme(this.currentTheme);
    this.setAppTheme(newTheme);
    window.localStorage[this.storageKey] = newTheme;
  }
}
